import { create } from 'zustand';
import storage from '../native/storage.js';

/**
 * Per-device parent settings + the ephemeral parent-area unlock.
 *
 *  - `deviceRole` ('child' | 'parent') — persisted. A 'child' device boots into
 *    the kid play UI and gates the parent area; a 'parent' device boots straight
 *    into the parent area.
 *  - `gateDefaultMemberId` — persisted. The household member the gate mails its
 *    code to by default (the last choice the parent made).
 *  - `unlockedUntil` — EPHEMERAL (never persisted), an epoch-ms deadline. On a
 *    child device the parent area is open while `now < unlockedUntil`; a fresh
 *    interactive sign-in or a passed gate sets it. Parent devices ignore this
 *    and derive their unlock from being signed in (see ParentGate).
 *
 * Why a store (vs a useStorage hook): the role + unlock drive both App boot
 * routing and the gate, so they must propagate instantly across the app.
 */

const ROLE_KEY = 'lexonauts.device.role';
const DEFAULT_MEMBER_KEY = 'lexonauts.device.gateDefaultMember';

export const DEVICE_ROLES = ['child', 'parent'];
const isKnownRole = (r) => DEVICE_ROLES.includes(r);

// How long a child-device parent-area unlock lasts after passing the gate (or a
// fresh interactive sign-in). Parent devices stay unlocked for the whole
// session instead, so this window doesn't apply there.
export const UNLOCK_TTL_MS = 15 * 60 * 1000;

const initialState = {
  hydrated: false,
  deviceRole: 'child',
  gateDefaultMemberId: null,
  unlockedUntil: 0,
};

export const useDeviceModeStore = create((set, get) => ({
  ...initialState,

  /** Read persisted device settings once at app boot. Idempotent. */
  async hydrate() {
    if (get().hydrated) return;
    const [role, member] = await Promise.all([
      storage.getItem(ROLE_KEY),
      storage.getItem(DEFAULT_MEMBER_KEY),
    ]);
    set({
      deviceRole: isKnownRole(role) ? role : 'child',
      gateDefaultMemberId: typeof member === 'string' ? member : null,
      hydrated: true,
    });
  },

  async setDeviceRole(next) {
    if (!isKnownRole(next) || next === get().deviceRole) return;
    set({ deviceRole: next });
    await storage.setItem(ROLE_KEY, next);
  },

  async setGateDefaultMemberId(id) {
    const value = typeof id === 'string' && id.length > 0 ? id : null;
    if (value === get().gateDefaultMemberId) return;
    set({ gateDefaultMemberId: value });
    if (value) await storage.setItem(DEFAULT_MEMBER_KEY, value);
    else await storage.removeItem(DEFAULT_MEMBER_KEY);
  },

  /** Open the parent area for the child-device unlock window. */
  unlock(now = Date.now()) {
    set({ unlockedUntil: now + UNLOCK_TTL_MS });
  },

  /** Re-lock immediately (sign-out). */
  lock() {
    set({ unlockedUntil: 0 });
  },

  /** Test/dev helper: reset to defaults and clear storage. */
  async reset() {
    set({ ...initialState });
    await Promise.all([storage.removeItem(ROLE_KEY), storage.removeItem(DEFAULT_MEMBER_KEY)]);
  },
}));

export const selectDeviceRole = (s) => s.deviceRole;
export const selectGateDefaultMemberId = (s) => s.gateDefaultMemberId;
export const selectDeviceHydrated = (s) => s.hydrated;
export const selectUnlockedUntil = (s) => s.unlockedUntil;

/** Convenience for App.jsx boot — mirrors `hydrateUIVariantStore()`. */
export function hydrateDeviceModeStore() {
  return useDeviceModeStore.getState().hydrate();
}
